import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { ButtonBase, Symbol } from "@faire/retailer-visitor-shared/components/Button/RoundButtonStyles";
import { FadeAnimatedContainer } from "@faire/retailer-visitor-shared/components/RoundQuantityPicker/__internal__/FadeAnimatedContainer";
import { RoundMultiOptionPicker } from "@faire/retailer-visitor-shared/components/RoundQuantityPicker/RoundMultiOptionPicker";
import { useRoundQuantityPickerState } from "@faire/retailer-visitor-shared/components/RoundQuantityPicker/UseRoundQuantityPickerState";
import { useRetailerStore } from "@faire/retailer-visitor-shared/stores/domain/RetailerStore";
import { debounceIfPossible } from "@faire/web--source/common/globals/debounceIfPossible";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web--source/common/localization/localize";
import { Add } from "@faire/web--source/slate/icons/foundation/Add";
import { Subtract } from "@faire/web--source/slate/icons/foundation/Subtract";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { Span } from "@faire/web--source/slate/Typography";
import ClickOutside from "@faire/web--source/ui/ClickOutside";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { trackRetailersRetailerOnChooseOptionFromQuickerAddClick } from "@faire/web-api--source/events/retailers/click/retailerOnChooseOptionFromQuickerAdd";
import React from "react";
import styled, { css } from "styled-components";
// 80 px provides the space for rendering
// - 2 side buttons: +(32px) / -(32px)
// - 2 paddings(each 8px)
//   (+ (padding) {digit} (padding) -)
var computeWidth = function(stringLength) {
    return stringLength * 8 + 80;
};
// If the value string is <= 2 digits, we want to show the round circle style, 32 px is to make sure we can render the round border radius correctly
// if number of digits > 2, we want to increment the width with 8px per digit
// ( single-digit ) <- 32 px width, 0 px reserved for digits
// ( xxx ) <- 40 px width, 32 px for round borders, 8 px for number
var computeBaseWidth = function(stringLength) {
    return (stringLength > 2 ? stringLength - 2 : 0) * 8 + 32;
};
var FADE_ANIMATION_TIME_MS = 200;
/**
 * @trackcomponent
 */ export var RoundQuantityPicker = function(param) {
    var onChange = param.onChange, value = param.value, minValue = param.minValue, maxValue = param.maxValue, stepSize = param.stepSize, disabled = param.disabled, size = param.size, error = param.error, onIncrementEvent = param.onIncrementEvent, onDecrementEvent = param.onDecrementEvent, isMultiOption = param.isMultiOption, onChooseMultiOption = param.onChooseMultiOption, maxWidth = param.maxWidth, showModalOnClick = param.showModalOnClick, multiOptionText = param.multiOptionText, onMouseLeave = param.onMouseLeave;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _React_useState = _sliced_to_array(React.useState(error ? true : false), 2), expanded = _React_useState[0], setExpanded = _React_useState[1];
    var _React_useState1 = _sliced_to_array(React.useState(false), 2), shouldDisableHoverExpand = _React_useState1[0], setShouldDisableHoverExpand = _React_useState1[1];
    var debouncedClose = React.useMemo(function() {
        return debounceIfPossible(function() {
            return setExpanded(false);
        }, 3000);
    }, []);
    React.useEffect(function() {
        if (expanded) {
            debouncedClose();
        }
    }, [
        value,
        expanded,
        debouncedClose
    ]);
    var _useRoundQuantityPickerState = _sliced_to_array(useRoundQuantityPickerState({
        stepSize: stepSize,
        minValue: minValue,
        maxValue: maxValue,
        value: value,
        onChange: onChange
    }), 2), pickerValue = _useRoundQuantityPickerState[0], dispatch = _useRoundQuantityPickerState[1];
    var componentSize = getSpacing(size !== null && size !== void 0 ? size : "4x");
    var prevPickerValueLength = React.useRef(-1);
    var _React_useState2 = _sliced_to_array(React.useState(computeWidth(String(pickerValue).length)), 2), computedWidth = _React_useState2[0], setComputedWidth = _React_useState2[1];
    var _React_useState3 = _sliced_to_array(React.useState(computeBaseWidth(String(pickerValue).length)), 2), computedTextWidth = _React_useState3[0], setComputedTextWidth = _React_useState3[1];
    var retailerToken = useRetailerStore([
        "retailerToken"
    ]).retailerToken;
    var _useViewport = useViewport(), isDesktopAndAbove = _useViewport.isDesktopAndAbove, isTabletAndBelow = _useViewport.isTabletAndBelow;
    React.useEffect(function() {
        var curPickerValueLength = String(pickerValue).length;
        if (curPickerValueLength !== prevPickerValueLength.current) {
            prevPickerValueLength.current = curPickerValueLength;
            setComputedWidth(computeWidth(String(pickerValue).length));
            setComputedTextWidth(computeBaseWidth(String(pickerValue).length));
        }
    }, [
        pickerValue
    ]);
    if (isMultiOption) {
        return /*#__PURE__*/ _jsx(RoundMultiOptionPicker, {
            textWidth: computedTextWidth,
            "data-test-id": "quick-add-quantity-picker-multi-option",
            value: value,
            size: size,
            onChooseMultiOption: function() {
                trackRetailersRetailerOnChooseOptionFromQuickerAddClick(retailerToken !== null && retailerToken !== void 0 ? retailerToken : "");
                onChooseMultiOption === null || onChooseMultiOption === void 0 ? void 0 : onChooseMultiOption();
            },
            maxWidth: maxWidth,
            multiOptionText: multiOptionText,
            showModalOnClick: !!showModalOnClick
        });
    }
    var expandComponent = function() {
        if (!shouldDisableHoverExpand) {
            if (!disabled) {
                setExpanded(true);
            }
        }
    };
    var renderContent = function() {
        if (value === 0 && !expanded) {
            return /*#__PURE__*/ _jsx(ButtonBase, {
                "data-test-id": "round-quantity-picker-expand-button",
                $size: componentSize,
                onClick: function(e) {
                    e.preventDefault();
                    expandComponent();
                    dispatch("increment");
                    onIncrementEvent === null || onIncrementEvent === void 0 ? void 0 : onIncrementEvent();
                },
                "aria-label": strictLocalize({
                    id: "W96gyL",
                    defaultMessage: [
                        {
                            type: 6,
                            value: "numberOfProductsAmount",
                            options: {
                                one: {
                                    value: [
                                        {
                                            type: 0,
                                            value: "click to add "
                                        },
                                        {
                                            type: 7
                                        },
                                        {
                                            type: 0,
                                            value: " more product"
                                        }
                                    ]
                                },
                                other: {
                                    value: [
                                        {
                                            type: 0,
                                            value: "click to add "
                                        },
                                        {
                                            type: 7
                                        },
                                        {
                                            type: 0,
                                            value: " more products"
                                        }
                                    ]
                                }
                            },
                            offset: 0,
                            pluralType: "cardinal"
                        }
                    ]
                }, DEPRECATED_backwardsCompatibleValues({
                    numberOfProductsAmount: stepSize
                })),
                onMouseLeave: function() {
                    setShouldDisableHoverExpand(false);
                    onMouseLeave === null || onMouseLeave === void 0 ? void 0 : onMouseLeave();
                },
                disabled: disabled,
                children: /*#__PURE__*/ _jsx(Symbol, {
                    children: /*#__PURE__*/ _jsx(Add, {})
                })
            });
        } else if (!expanded) {
            return /*#__PURE__*/ _jsx(ButtonBase, {
                "data-test-id": "round-quantity-picker-expand-button-with-number",
                $size: componentSize,
                style: {
                    "--size": componentSize
                },
                "aria-label": strictLocalize({
                    id: "bBK1Zb",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "quantity picker"
                        }
                    ]
                }),
                onMouseEnter: function() {
                    if (isDesktopAndAbove) {
                        setExpanded(true);
                    }
                },
                onClick: function(e) {
                    if (isTabletAndBelow) {
                        e.preventDefault();
                        e.stopPropagation();
                        setExpanded(true);
                    }
                },
                disabled: disabled,
                children: /*#__PURE__*/ _jsx(Span, {
                    variant: "paragraphSansMedium",
                    style: {
                        color: "inherit"
                    },
                    children: pickerValue
                })
            });
        } else {
            return /*#__PURE__*/ _jsxs(Row, {
                "data-test-id": "round-quantity-picker-main-content",
                justify: "space-between",
                align: "center",
                style: {
                    minWidth: "".concat(computedWidth, "px")
                },
                onMouseLeave: function() {
                    setExpanded(false);
                    setShouldDisableHoverExpand(false);
                    onMouseLeave === null || onMouseLeave === void 0 ? void 0 : onMouseLeave();
                },
                children: [
                    /*#__PURE__*/ _jsx(ButtonBase, {
                        "data-test-id": "round-quantity-picker-decrement-button",
                        $size: componentSize,
                        style: {
                            "--size": componentSize
                        },
                        onClick: function(e) {
                            e.preventDefault();
                            dispatch("decrement");
                            onDecrementEvent === null || onDecrementEvent === void 0 ? void 0 : onDecrementEvent();
                        },
                        "aria-label": strictLocalize({
                            id: "pJ0G5s",
                            defaultMessage: [
                                {
                                    type: 6,
                                    value: "numberOfProductsAmount",
                                    options: {
                                        one: {
                                            value: [
                                                {
                                                    type: 0,
                                                    value: "click to remove "
                                                },
                                                {
                                                    type: 7
                                                },
                                                {
                                                    type: 0,
                                                    value: " product"
                                                }
                                            ]
                                        },
                                        other: {
                                            value: [
                                                {
                                                    type: 0,
                                                    value: "click to remove "
                                                },
                                                {
                                                    type: 7
                                                },
                                                {
                                                    type: 0,
                                                    value: " more products"
                                                }
                                            ]
                                        }
                                    },
                                    offset: 0,
                                    pluralType: "cardinal"
                                }
                            ]
                        }, DEPRECATED_backwardsCompatibleValues({
                            numberOfProductsAmount: stepSize
                        })),
                        children: /*#__PURE__*/ _jsx(FadeAnimatedContainer, {
                            willRender: expanded,
                            fadeInDuration: FADE_ANIMATION_TIME_MS,
                            children: /*#__PURE__*/ _jsx(Symbol, {
                                children: /*#__PURE__*/ _jsx(Subtract, {
                                    color: Core.text.inverse
                                })
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(Span, {
                        variant: "paragraphSansMedium",
                        style: {
                            color: "inherit"
                        },
                        children: pickerValue
                    }),
                    /*#__PURE__*/ _jsx(ButtonBase, {
                        "data-test-id": "round-quantity-picker-increment-button",
                        $size: componentSize,
                        style: {
                            "--size": componentSize
                        },
                        "aria-label": strictLocalize({
                            id: "W96gyL",
                            defaultMessage: [
                                {
                                    type: 6,
                                    value: "numberOfProductsAmount",
                                    options: {
                                        one: {
                                            value: [
                                                {
                                                    type: 0,
                                                    value: "click to add "
                                                },
                                                {
                                                    type: 7
                                                },
                                                {
                                                    type: 0,
                                                    value: " more product"
                                                }
                                            ]
                                        },
                                        other: {
                                            value: [
                                                {
                                                    type: 0,
                                                    value: "click to add "
                                                },
                                                {
                                                    type: 7
                                                },
                                                {
                                                    type: 0,
                                                    value: " more products"
                                                }
                                            ]
                                        }
                                    },
                                    offset: 0,
                                    pluralType: "cardinal"
                                }
                            ]
                        }, DEPRECATED_backwardsCompatibleValues({
                            numberOfProductsAmount: stepSize
                        })),
                        onClick: function(e) {
                            e.preventDefault();
                            dispatch("increment");
                            onIncrementEvent === null || onIncrementEvent === void 0 ? void 0 : onIncrementEvent();
                        },
                        children: /*#__PURE__*/ _jsx(FadeAnimatedContainer, {
                            willRender: expanded,
                            fadeInDuration: FADE_ANIMATION_TIME_MS,
                            children: /*#__PURE__*/ _jsx(Symbol, {
                                children: /*#__PURE__*/ _jsx(Add, {
                                    color: Core.text.inverse
                                })
                            })
                        })
                    })
                ]
            });
        }
    };
    return /*#__PURE__*/ _jsx(ClickOutside, {
        onClickOutside: error ? undefined : function() {
            return setExpanded(false);
        },
        "data-test-id": "quick-add-quantity-picker-single-option",
        children: /*#__PURE__*/ _jsx(Column, {
            children: /*#__PURE__*/ _jsx(AnimatedBackground, {
                $maxWidth: computedWidth,
                $isBase: value === 0 && !expanded,
                $expand: expanded,
                $size: componentSize,
                $textWidth: computedTextWidth,
                children: renderContent()
            })
        })
    });
};
var AnimatedBackground = styled.div.withConfig({
    componentId: "sc-e875bb0f-0"
})([
    "display:flex;align-items:center;justify-content:center;padding-left:",
    ";padding-right:",
    ";font-size:16px;height:",
    ";background-color:",
    ";transition:all 200ms;transition-timing-function:ease-in-out;width:",
    ";border-radius:16px;color:",
    ";",
    ";"
], function(props) {
    return props.$expand ? getSpacing("2x") : "0";
}, function(props) {
    return props.$expand ? getSpacing("2x") : "0";
}, function(props) {
    return props.$size;
}, function(props) {
    return props.$isBase ? Core.surface.inverse : Core.surface.default;
}, function(props) {
    return(// eslint-disable-next-line no-nested-ternary
    props.$expand ? css([
        "",
        "px"
    ], props.$maxWidth) : props.$isBase ? props.$size : css([
        "",
        "px"
    ], props.$textWidth));
}, function(props) {
    return props.$isBase ? Core.text.default : Core.text.inverse;
}, function(param) {
    var $isBase = param.$isBase;
    return $isBase ? css([
        "&:hover{background-color:",
        ";color:",
        ";}"
    ], Core.surface.default, Core.border.muted) : null;
});
